// utils/fileHelpers.js
import secureAxios from "./secureAxios";

/**
 * Generate a preview URL for a file
 * This should be used with audit logging when accessing PHI documents
 *
 * @param {string} filePath - The file path
 * @param {string} apiBase - The API base URL (optional, will use secureAxios default)
 * @returns {string} The preview URL
 */
export const getPreviewUrlFromPath = (filePath, apiBase) => {
  if (!filePath) {
    console.warn("No file path provided for preview URL");
    return "";
  }

  const filename = filePath.split("/").pop();
  const baseUrl = apiBase || secureAxios.defaults.baseURL;

  // Ensure the URL is properly encoded
  return `${baseUrl}/api/documents/preview/filename/${encodeURIComponent(
    filename
  )}`;
};

/**
 * Check if a file contains potential PHI based on filename patterns
 * This is a helper to remind developers to use audit logging
 *
 * @param {string} filename - The filename to check
 * @returns {boolean} True if filename suggests PHI content
 */
export const isPotentialPHIFile = (filename) => {
  if (!filename) return false;

  const phiPatterns = [
    /medical/i,
    /patient/i,
    /health/i,
    /diagnosis/i,
    /treatment/i,
    /prescription/i,
    /lab[\s_-]?result/i,
    /xray/i,
    /scan/i,
    /record/i,
    /chart/i,
    /insurance/i,
    /claim/i,
    /billing/i,
    /confidential/i,
    /private/i,
    /hipaa/i,
    /phi/i,
  ];

  return phiPatterns.some((pattern) => pattern.test(filename));
};

/**
 * Get file metadata with audit logging support
 * Use this when accessing files that may contain PHI
 *
 * @param {string} fileId - The file ID
 * @param {Object} auditInfo - Audit information for HIPAA compliance
 * @returns {Promise} File metadata
 */
export const getFileMetadata = async (fileId, auditInfo = null) => {
  // Check if audit info should be provided
  if (!auditInfo) {
    console.warn(
      "⚠️ No audit information provided for file access. " +
        "If this file contains PHI, please provide audit context."
    );
  }

  try {
    const response = await secureAxios.get(`/api/files/metadata/${fileId}`);

    // Log if this appears to be PHI
    if (response.data && isPotentialPHIFile(response.data.filename)) {
      console.warn(
        "⚠️ This file may contain PHI. Ensure proper audit logging is in place."
      );
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching file metadata:", error);
    throw error;
  }
};

/**
 * Download a file with audit logging support
 *
 * @param {string} fileId - The file ID
 * @param {string} filename - The filename for download
 * @param {Object} auditInfo - Audit information for HIPAA compliance
 * @returns {Promise} File download
 */
export const downloadFile = async (fileId, filename, auditInfo = null) => {
  // Warn if no audit info for potential PHI
  if (isPotentialPHIFile(filename) && !auditInfo) {
    console.error(
      "❌ Attempting to download potential PHI file without audit context"
    );
    throw new Error("Audit context required for PHI file download");
  }

  try {
    const response = await secureAxios.get(`/api/files/download/${fileId}`, {
      responseType: "blob",
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * Upload a file with automatic PHI detection warning
 *
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @returns {Promise} Upload response
 */
export const uploadFile = async (file, options = {}) => {
  // Check if filename suggests PHI
  if (isPotentialPHIFile(file.name)) {
    console.warn(
      "⚠️ Uploading file that may contain PHI. Ensure proper security measures are in place."
    );
  }

  const formData = new FormData();
  formData.append("file", file);

  // Add any additional options to form data
  Object.keys(options).forEach((key) => {
    formData.append(key, options[key]);
  });

  try {
    const response = await secureAxios.post("/api/files/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

/**
 * Delete a file with audit logging
 *
 * @param {string} fileId - The file ID to delete
 * @param {Object} auditInfo - Audit information for HIPAA compliance
 * @returns {Promise} Delete response
 */
export const deleteFile = async (fileId, auditInfo) => {
  if (!auditInfo) {
    console.warn(
      "⚠️ Deleting file without audit context. This should be logged for HIPAA compliance."
    );
  }

  try {
    const response = await secureAxios.delete(`/api/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting file:", error);
    throw error;
  }
};

// Export all functions
export default {
  getPreviewUrlFromPath,
  isPotentialPHIFile,
  getFileMetadata,
  downloadFile,
  uploadFile,
  deleteFile,
};
